export type RankingBadgeInput = {
  rankingTier: number | null;
  rankingType: string | null;
};

const NO_RANKING_LABEL = "—";

const CATEGORY_LABELS: Record<string, string> = {
  "firm to_watch": "Firms to Watch",
  "firm recommended": "Recommended",
};

function titleCaseFromRaw(rankingType: string): string {
  return rankingType
    .split(/[\s_]+/)
    .filter(word => word.length > 0)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(" ");
}

function untieredCategoryLabel(rankingType: string | null): string {
  if (rankingType === null) {
    return NO_RANKING_LABEL;
  }
  return CATEGORY_LABELS[rankingType] ?? titleCaseFromRaw(rankingType);
}

function isAlwaysCategoryLabelled(rankingType: string | null): boolean {
  return rankingType === "firm to_watch";
}

/**
 * Derives the label shown by the org list's ranking badge.
 *
 * A firm can be in one of four states: unranked (no tier at all, show an em
 * dash), a category that is always shown by name regardless of tier (Firms
 * to Watch is its own band, not a tier ladder), untiered-but-ranked (tier 0,
 * show the ranking category's display name), or tiered (tier 1-8, show the
 * number). Tier 0 must never be confused with "no ranking" — it is a real
 * ranking that simply carries no tier — so this checks for `null` explicitly
 * rather than falsiness.
 */
export function rankingBadgeLabel(ranking: RankingBadgeInput): string {
  if (ranking.rankingTier === null) {
    return NO_RANKING_LABEL;
  }
  if (isAlwaysCategoryLabelled(ranking.rankingType)) {
    return untieredCategoryLabel(ranking.rankingType);
  }
  if (ranking.rankingTier === 0) {
    return untieredCategoryLabel(ranking.rankingType);
  }
  return String(ranking.rankingTier);
}
